// src/pages/KitabPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { dbAdmin } from "../firebaseConfig";
import "../styles/Kitab.css";
import KitabView from "../components/KitabView";
import PageBackBar from "../components/PageBackBar";
import { getCachedPublicMasjids, loadPublicMasjids } from "../services/publicMasjidCatalog";

const LS_KITABS = "v2_kitab_list_cache";

const norm = (s = "") => String(s || "").trim();

export default function KitabPage() {
    const { kitabId } = useParams();
    const navigate = useNavigate();

    const [kitabs, setKitabs] = useState([]);
    const [masjids, setMasjids] = useState([]);
    const [activeTab, setActiveTab] = useState("Semua");
    const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

    // 1) cache first
    useEffect(() => {
        try {
            const k = JSON.parse(localStorage.getItem(LS_KITABS) || "[]");
            const m = getCachedPublicMasjids();
            if (Array.isArray(k)) setKitabs(k);
            if (Array.isArray(m)) setMasjids(m);
        } catch { }
    }, []);

    // 2) subscribe Firestore (latest)
    useEffect(() => {
        const qKitab = query(collection(dbAdmin, "kitabs"), orderBy("normalizedTitle", "asc"));
        const unsubKitab = onSnapshot(
            qKitab,
            (snap) => {
                const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setKitabs(rows);
                try { localStorage.setItem(LS_KITABS, JSON.stringify(rows)); } catch { }
            },
            (err) => console.error("❌ load kitabs error:", err)
        );

        let active = true;
        loadPublicMasjids()
            .then((rows) => {
                if (active) setMasjids(rows);
            })
            .catch((err) => console.error("❌ load masajid error:", err));

        return () => {
            active = false;
            unsubKitab();
        };
    }, []);

    // responsive: mobile hide list when selected
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 900);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const selectedKitab = useMemo(() => {
        if (!kitabId) return null;
        return kitabs.find((k) => String(k.id) === String(kitabId)) || null;
    }, [kitabId, kitabs]);

    // tabs by category (subjek)
    const tabs = useMemo(() => {
        const set = new Set();
        kitabs.forEach((k) => set.add(norm(k.category || "Umum") || "Umum"));
        return ["Semua", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
    }, [kitabs]);

    // keep active tab valid
    useEffect(() => {
        if (!tabs.includes(activeTab)) setActiveTab("Semua");
    }, [tabs, activeTab]);

    const listByTab = useMemo(() => {
        if (activeTab === "Semua") return kitabs;
        return kitabs.filter((k) => norm(k.category || "Umum") === activeTab);
    }, [kitabs, activeTab]);

    const onPick = (k) => navigate(`/kitab/${k.id}`);

    const showList = !(isMobile && kitabId); // ✅ mobile: bila ada kitabId, hide list
    const showDetail = !isMobile || Boolean(kitabId); // desktop always show detail area

    return (
        <div className="kitab-page2">
            <PageBackBar title="Kitab" />


            {/* Tabs */}
            <div className="kitab-tabs">
                {tabs.map((t) => (
                    <button
                        key={t}
                        type="button"
                        className={`kitab-tab ${activeTab === t ? "active" : ""}`}
                        onClick={() => setActiveTab(t)}
                    >
                        {t}
                    </button>
                ))}
            </div>

            <div className="kitab-layout">
                {/* List */}
                {showList && (
                    <aside className="kitab-list">
                        {listByTab.length === 0 ? (
                            <div className="kitab-empty">Tiada kitab dalam kategori ini.</div>
                        ) : (
                            <div className="kitab-list-grid">
                                {listByTab.map((k) => {
                                    const active = String(k.id) === String(kitabId);
                                    const title = k.title || k.name || k.id;

                                    return (
                                        <button
                                            key={k.id}
                                            type="button"
                                            className={`kitab-card-mini ${active ? "active" : ""}`}
                                            onClick={() => onPick(k)}
                                        >
                                            {k.coverImage ? (
                                                <img className="kitab-mini-cover" src={k.coverImage} alt={title} onError={(e) => (e.currentTarget.style.display = "none")} />
                                            ) : (
                                                <div className="kitab-mini-cover fallback" />
                                            )}
                                            <div className="kitab-mini-text">
                                                <div className="kitab-mini-title">{title}</div>
                                                <div className="kitab-mini-meta">
                                                    {(k.category || "Umum") + (k.author ? ` • ${k.author}` : "")}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </aside>
                )}

                {/* Detail */}
                {showDetail && (
                    <section className="kitab-detail">
                        {!kitabId ? (
                            <div className="kitab-empty">Pilih kitab.</div>
                        ) : !selectedKitab ? (
                            <div className="kitab-empty">Kitab tidak dijumpai.</div>
                        ) : (
                            <KitabView kitab={selectedKitab} v2Masjids={masjids} onClose={() => navigate("/kitab")} />
                        )}
                    </section>
                )}
            </div>
        </div>
    );
}
